import {
  MarketingTriggerKey,
  OrderTrackingTriggerKey,
  type TriggerKey,
} from "@redotech/redo-flows-common/triggers";
import { SkillId } from "../skill-id";
import type { TriggerDefinition } from "../trigger-definition";
import {
  POST_PURCHASE_CATEGORIES,
  PRE_PURCHASE_CATEGORIES,
  TriggerCategory,
  TriggerConversationType,
} from "../trigger-definition";

// ---------------------------------------------------------------------------
// Unsupported triggers — intentionally NOT handled by the shopper agent.
//
// If you want to support a trigger, remove it from this map and add a
// TriggerDefinition below. The triggers.spec.ts test enforces that every
// TriggerKey is either defined here or listed as unsupported.
// ---------------------------------------------------------------------------

const unsupportedEntries: [TriggerKey, string][] = [
  // No product selection UI exists yet — merchants can't configure which
  // products to monitor, so enabling this would do nothing useful.
  [MarketingTriggerKey.LOW_INVENTORY, "No product selection UI exists yet"],

  // Campaign and date triggers are manual/scheduled sends, not event-driven.
  // They don't fit the shopper agent's reactive model.
  [
    MarketingTriggerKey.MARKETING_CAMPAIGN,
    "Manual/scheduled send, not event-driven",
  ],
  [MarketingTriggerKey.DATE, "Date-based scheduled send, not event-driven"],
  [
    MarketingTriggerKey.GIFT_CARD_BALANCE,
    "Scheduled balance scan, not event-driven",
  ],

  // Customer group triggers need segment evaluation infrastructure that
  // isn't wired into the marketing agent coordinator yet.
  [
    MarketingTriggerKey.CUSTOMER_GROUP_ENTERED,
    "Segment evaluation not wired to marketing agent",
  ],
  [
    MarketingTriggerKey.CUSTOMER_GROUP_EXITED,
    "Segment evaluation not wired to marketing agent",
  ],

  // Post-purchase upsell is a conversion product feature, not a messaging trigger.
  [
    OrderTrackingTriggerKey.POST_PURCHASE_UPSELL,
    "Conversion product feature, not a messaging trigger",
  ],

  // INBOUND_RECEIVED is dispatched by the marketing inbound-message pipeline
  // (see redo/marketing/service/src/event-pipeline/handlers/outreach/
  // inbound-message-utils.ts) directly into the marketing-agent coordinator
  // workflow. The enum value is load-bearing — it's used as an event-type
  // discriminator (IMMEDIATE_EVENT_TYPES, REPLY-vs-INITIAL message type,
  // triggerToMarketingAgentCustomerEventType) — but there's no per-trigger
  // configuration to expose: eligibility is gated by `shopperAgent.replyConfig`
  // plus the support stack via `isShopperAgentReplyEnabled`, and the playbook
  // surfaces replies under a dedicated Brand → Reply page rather than as a
  // playbook trigger. So the trigger has no TriggerDefinition.
  [
    MarketingTriggerKey.INBOUND_RECEIVED,
    "Routed by inbound-message-utils and gated by shopperAgent.replyConfig — not configurable per-trigger",
  ],

  // Subscription cancellation is handled by a dedicated Recharge flow trigger,
  // not by the shopper-agent. Requires a Recharge integration to fire.
  [
    MarketingTriggerKey.PRODUCT_SUBSCRIPTION_CANCELLED,
    "Handled by Recharge subscription-cancelled flow, not shopper-agent",
  ],
];

export const UNSUPPORTED_TRIGGERS: ReadonlyMap<TriggerKey, string> = new Map(
  unsupportedEntries,
);

// ---------------------------------------------------------------------------
// Cart & Checkout
// ---------------------------------------------------------------------------

const checkoutAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.CHECKOUT_ABANDONED,
  name: "Checkout Abandoned",
  description: "Customer started checkout but didn't complete the purchase",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const cartAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.CART_ABANDONED,
  name: "Cart Abandoned",
  description: "Customer added items to cart but didn't proceed to checkout",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const browseAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.BROWSE_ABANDONED,
  name: "Browse Abandoned",
  description: "Customer browsed products but didn't add anything to cart",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const emailSignupTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.EMAIL_SIGNUP,
  name: "Email Signup",
  description: "Customer signed up for email marketing",
  category: TriggerCategory.SIGNUPS,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance: `The trigger for this outreach is an **email signup** — this is a welcome moment.
- This is the customer's first impression of the brand's communication
- If a forced discount is present, feature it prominently — they signed up expecting it
- Set the tone for future communications: warm, helpful, on-brand
- Introduce the brand value proposition alongside any welcome offer`,
  processingPolicy: {
    buffer: { defaultBypass: true },
    sentinel: { defaultBypass: true },
  },
};

const smsSignupTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.SMS_SIGNUP,
  name: "SMS Signup",
  description: "Customer confirmed SMS marketing subscription",
  category: TriggerCategory.SIGNUPS,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  // SMS regulation requires that confirmation/welcome flows always fire —
  // the shopper agent bypass for SMS_SIGNUP is hardcoded in
  // marketing-agent-routing.ts. Disallow attaching automations here so the
  // merchant can't configure a state that would never be consulted.
  supportsAutomations: false,
  // Same reasoning — the coordinator handoff for SMS_SIGNUP happens in
  // MarketingConfirmedHandler with no SchemaInstance in scope, so skip
  // conditions can't be evaluated even if persisted. Lock the field to
  // prevent merchants from configuring something that would never apply.
  supportsSkipConditions: false,
  skills: [SkillId.DISCOUNT],
  guidance: `The trigger for this outreach is an **SMS signup** — this is a welcome moment.
- This is the customer's first impression of the brand's SMS communication
- If a forced discount is present, feature it prominently — they signed up expecting it
- Keep it concise and valuable — respect the intimacy of the SMS channel
- Introduce the brand briefly alongside any welcome offer`,
  processingPolicy: {
    buffer: { defaultBypass: true },
    sentinel: { defaultBypass: true },
  },
};

const priceDropTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.PRICE_DROP,
  name: "Price Drop",
  description: "A product the customer showed interest in dropped in price",
  category: TriggerCategory.PRODUCT_INTEREST,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance: `The trigger for this outreach is a **price drop** on a product the customer has shown interest in.
- Lead with the before/after price and the savings amount or percentage — this is the headline
- The price drop itself is the incentive; lean against stacking an additional discount on top
- Create urgency around the new lower price (prices can go back up, limited-time pricing)
- Reference the specific product and the customer's prior engagement with it`,
};

const backInStockTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.BACK_IN_STOCK,
  name: "Back in Stock",
  description: "An out-of-stock product the customer wanted is available again",
  category: TriggerCategory.PRODUCT_INTEREST,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance: `The trigger for this outreach is a **back-in-stock** notification.
- Lead with availability — the product the customer wanted is available again
- Create scarcity urgency: this item sold out before and could sell out again
- No discount is needed — availability itself is the value proposition
- Reference the specific product and the customer's prior interest (waitlist, browse history)`,
  processingPolicy: {
    buffer: { defaultBypass: true },
    sentinel: { defaultBypass: true },
  },
};

const emailSignupShopifyTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.EMAIL_SIGNUP_SHOPIFY,
  name: "Email Signup (Shopify)",
  description: "Customer signed up for email marketing via Shopify",
  category: TriggerCategory.SIGNUPS,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance: emailSignupTrigger.guidance,
  processingPolicy: emailSignupTrigger.processingPolicy,
};

const bigcommerceCartAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.BIGCOMMERCE_CART_ABANDONED,
  name: "Cart Abandoned (BigCommerce)",
  description:
    "BigCommerce customer added items to cart but didn't proceed to checkout",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const bigcommerceCheckoutAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.BIGCOMMERCE_CHECKOUT_ABANDONED,
  name: "Checkout Abandoned (BigCommerce)",
  description:
    "BigCommerce customer started checkout but didn't complete the purchase",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const commentsoldCartAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.COMMENTSOLD_CART_ABANDONED,
  name: "Cart Abandoned (CommentSold)",
  description:
    "CommentSold customer added items to cart but didn't proceed to checkout",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const commentsoldBrowseAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.COMMENTSOLD_BROWSE_ABANDONED,
  name: "Browse Abandoned (CommentSold)",
  description: "CommentSold customer browsed products but didn't add to cart",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const commentsoldCheckoutAbandonedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.COMMENTSOLD_CHECKOUT_ABANDONED,
  name: "Checkout Abandoned (CommentSold)",
  description:
    "CommentSold customer started checkout but didn't complete the purchase",
  category: TriggerCategory.CART_CHECKOUT,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

// ---------------------------------------------------------------------------
// Returns & Warranty
// ---------------------------------------------------------------------------

const refundReturnSubmittedTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.REFUND_RETURN_SUBMITTED,
  name: "Refund Return Processed",
  description: "Customer's refund return has finished processing",
  category: TriggerCategory.RETURNS_WARRANTY,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance: `The trigger for this outreach is a **refund return that has been processed** — the customer's refund return has completed.
- Lead with empathy — acknowledge the return and express care for their experience
- Suggest alternative products they might prefer based on what they're returning
- A modest discount can work as a goodwill gesture to retain them, but keep it reasonable
- The goal is retention and relationship preservation, not a hard sell`,
};

const exchangeProcessedWithCreditTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.EXCHANGE_PROCESSED_WITH_CREDIT,
  name: "Exchange Processed with Credit",
  description: "Customer has leftover store credit from an exchange",
  category: TriggerCategory.RETURNS_WARRANTY,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  guidance: `The trigger for this outreach is an **exchange processed with leftover store credit**.
- Lead with the store credit amount — the customer has money to spend
- No additional discount is needed — the credit itself is the incentive
- Suggest products that complement their exchange or match their preferences
- Create a helpful shopping-assistant tone, not a promotional one`,
};

const warrantyRegistrationTrigger: TriggerDefinition = {
  id: MarketingTriggerKey.WARRANTY_REGISTRATION,
  name: "Warranty Registration",
  description: "Customer registered a product warranty",
  category: TriggerCategory.RETURNS_WARRANTY,
  conversationType: TriggerConversationType.CONCIERGE,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderCreatedTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_CREATED,
  name: "Order Created",
  description: "Customer placed a new order",
  category: TriggerCategory.ORDER_LIFECYCLE,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
  processingPolicy: {
    buffer: { defaultBypass: true, supportsOverride: true },
    sentinel: { defaultBypass: true, supportsOverride: true },
  },
};

const orderFulfilledTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_FULFILLED,
  name: "Order Fulfilled",
  description: "Order has been fulfilled and shipped",
  category: TriggerCategory.ORDER_LIFECYCLE,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderPreTransitTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_PRE_TRANSIT,
  name: "Pre-Transit",
  description: "Carrier has received the shipment",
  category: TriggerCategory.SHIPPING_UPDATES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderInTransitTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_IN_TRANSIT,
  name: "In Transit",
  description: "Package is moving through the carrier network",
  category: TriggerCategory.SHIPPING_UPDATES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderOutForDeliveryTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_OUT_FOR_DELIVERY,
  name: "Out for Delivery",
  description: "Package is out for delivery today",
  category: TriggerCategory.SHIPPING_UPDATES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderDeliveredTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_DELIVERED,
  name: "Order Delivered",
  description: "Package has been delivered",
  category: TriggerCategory.SHIPPING_UPDATES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderAvailableForPickupTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_AVAILABLE_FOR_PICKUP,
  name: "Available for Pickup",
  description: "Order is ready for customer pickup",
  category: TriggerCategory.PICKUP,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderAvailableForPickupCarrierTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_AVAILABLE_FOR_PICKUP_CARRIER,
  name: "Available for Pickup (Carrier)",
  description: "Order is ready for pickup at carrier location",
  category: TriggerCategory.PICKUP,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderStalledInTransitTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_STALLED_IN_TRANSIT,
  name: "Stalled in Transit",
  description: "Shipment appears to be stuck in transit",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderDelayedTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_DELAYED,
  name: "Order Delayed",
  description: "Shipment is running behind the estimated delivery date",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderStalledInFulfillmentTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_STALLED_IN_FULFILLMENT,
  name: "Stalled in Fulfillment",
  description: "Order hasn't shipped within the expected fulfillment window",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderDeliveryFailureTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_DELIVERY_FAILURE,
  name: "Delivery Failure",
  description: "Carrier could not deliver the package",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderDeliveryAttemptedTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_DELIVERY_ATTEMPTED,
  name: "Delivery Attempted",
  description: "Carrier attempted delivery but was unsuccessful",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderReturnToSenderTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_RETURN_TO_SENDER,
  name: "Return to Sender",
  description: "Package is being returned to the sender",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderShipmentCancelledTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_SHIPMENT_CANCELLED,
  name: "Shipment Cancelled",
  description: "Shipment has been cancelled",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderShipmentErrorTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_SHIPMENT_ERROR,
  name: "Shipment Error",
  description: "An error occurred with the shipment",
  category: TriggerCategory.SHIPPING_ISSUES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

const orderArrivingEarlyTrigger: TriggerDefinition = {
  id: OrderTrackingTriggerKey.ORDER_ARRIVING_EARLY,
  name: "Arriving Early",
  description: "Package is arriving earlier than expected",
  category: TriggerCategory.SHIPPING_UPDATES,
  conversationType: TriggerConversationType.MARKETING,
  defaultEnabled: false,
  skills: [SkillId.DISCOUNT],
};

// ---------------------------------------------------------------------------
// Aggregated lists — derived from category
// ---------------------------------------------------------------------------

export const ALL_TRIGGERS: TriggerDefinition[] = [
  // Pre-purchase
  checkoutAbandonedTrigger,
  cartAbandonedTrigger,
  browseAbandonedTrigger,
  emailSignupTrigger,
  smsSignupTrigger,
  priceDropTrigger,
  backInStockTrigger,
  emailSignupShopifyTrigger,
  bigcommerceCartAbandonedTrigger,
  bigcommerceCheckoutAbandonedTrigger,
  commentsoldCartAbandonedTrigger,
  commentsoldBrowseAbandonedTrigger,
  commentsoldCheckoutAbandonedTrigger,
  // Post-purchase
  refundReturnSubmittedTrigger,
  exchangeProcessedWithCreditTrigger,
  warrantyRegistrationTrigger,
  orderCreatedTrigger,
  orderFulfilledTrigger,
  orderPreTransitTrigger,
  orderInTransitTrigger,
  orderOutForDeliveryTrigger,
  orderDeliveredTrigger,
  orderAvailableForPickupTrigger,
  orderAvailableForPickupCarrierTrigger,
  orderStalledInTransitTrigger,
  orderDelayedTrigger,
  orderStalledInFulfillmentTrigger,
  orderDeliveryFailureTrigger,
  orderDeliveryAttemptedTrigger,
  orderReturnToSenderTrigger,
  orderShipmentCancelledTrigger,
  orderShipmentErrorTrigger,
  orderArrivingEarlyTrigger,
];

export const PRE_PURCHASE_TRIGGERS = ALL_TRIGGERS.filter((t) =>
  PRE_PURCHASE_CATEGORIES.includes(t.category),
);

export const POST_PURCHASE_TRIGGERS = ALL_TRIGGERS.filter((t) =>
  POST_PURCHASE_CATEGORIES.includes(t.category),
);

export const TRIGGERS_BY_CATEGORY: Record<
  TriggerCategory,
  TriggerDefinition[]
> = Object.fromEntries(
  Object.values(TriggerCategory).map((category) => [
    category,
    ALL_TRIGGERS.filter((t) => t.category === category),
  ]),
) as Record<TriggerCategory, TriggerDefinition[]>;

const triggersById = new Map<TriggerKey, TriggerDefinition>(
  ALL_TRIGGERS.map((trigger) => [trigger.id, trigger]),
);

export function getTriggerById(id: TriggerKey): TriggerDefinition | undefined {
  return triggersById.get(id);
}
